import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import { InputField, SelectField } from "@/components/ui/FormFields";
import {
  listNotifications,
  markRead,
  markAllRead,
  markDelivered,
  markOpened,
  markTapped,
  postFunnelEvent,
  type Notification,
  type FunnelName,
} from "@/api/notifications";
import { DevicesPanel } from "./DevicesPanel";

export default function NotificationsLab() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => listNotifications({ limit: 30 }),
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });

  return (
    <div className="space-y-8">
      <PageHeader
        title="Notifications"
        subtitle="Inbox + lifecycle markers (delivered/opened/tapped) + funnel-event tester."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Notifications" },
        ]}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={async () => {
              await markAllRead();
              refresh();
            }}
          >
            Mark all read
          </Button>
        }
      />

      <DevicesPanel />

      {/* Inbox + lifecycle */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Inbox</h2>
          {data && (
            <Badge variant={data.data.unreadCount > 0 ? "blue" : "gray"}>
              {data.data.unreadCount} unread
            </Badge>
          )}
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !data || data.data.notifications.length === 0 ? (
          <p className="text-sm text-gray-500">
            No notifications yet. Trigger some by doing actions in other Lab features.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.data.notifications.map((n) => (
              <NotificationRow key={n.id} n={n} onChange={refresh} />
            ))}
          </ul>
        )}
      </section>

      <FunnelTester />
    </div>
  );
}

function NotificationRow({
  n,
  onChange,
}: {
  n: Notification;
  onChange: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  async function run(action: "delivered" | "opened" | "tapped" | "read") {
    setBusy(action);
    setFeedback(null);
    try {
      if (action === "delivered") {
        const r = await markDelivered(n.id);
        setFeedback(
          r.firstTransition ? "Delivered (first)" : "Already delivered",
        );
      } else if (action === "opened") {
        const r = await markOpened(n.id);
        setFeedback(r.firstTransition ? "Opened (first)" : "Already opened");
      } else if (action === "tapped") {
        const r = await markTapped(n.id, n.actionUrl ?? undefined);
        setFeedback(r.firstTransition ? "Tapped (first)" : "Already tapped");
      } else if (action === "read") {
        await markRead(n.id);
        setFeedback("Marked read");
      }
      onChange();
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{n.title}</span>
            <Badge variant="gray">{n.type}</Badge>
            {!n.isRead && <Badge variant="blue">unread</Badge>}
          </div>
          {n.body && (
            <p className="text-sm text-gray-600 mt-0.5">{n.body}</p>
          )}
          <div className="text-xs text-gray-400 mt-1 font-mono">
            id: {n.id.slice(0, 8)}… · created{" "}
            {new Date(n.createdAt).toLocaleTimeString()}
          </div>
        </div>
        <div className="flex flex-wrap gap-1 shrink-0">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => run("delivered")}
            disabled={busy !== null}
          >
            {busy === "delivered" ? "…" : "Delivered"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => run("opened")}
            disabled={busy !== null}
          >
            {busy === "opened" ? "…" : "Opened"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => run("tapped")}
            disabled={busy !== null}
          >
            {busy === "tapped" ? "…" : "Tapped"}
          </Button>
        </div>
      </div>
      {feedback && (
        <div className="mt-1 text-xs text-blue-700">{feedback}</div>
      )}
    </li>
  );
}

function FunnelTester() {
  const [funnelName, setFunnelName] = useState<FunnelName>("identity_board");
  const [step, setStep] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const r = await postFunnelEvent({ funnelName, step });
      setResult(`Logged · id ${r.data.id.slice(0, 8)}…`);
      setStep("");
    } catch (err) {
      setResult(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-2xl p-5">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">
        Funnel event tester
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Posts to <code className="font-mono">/analytics/funnel-event</code>.
        Writes a <code className="font-mono">funnel_step</code> row + mirrors
        to PostHog.
      </p>
      <form onSubmit={handleSubmit} className="space-y-3 max-w-md">
        <SelectField
          label="Funnel"
          required
          value={funnelName}
          onChange={(e) => setFunnelName(e.target.value as FunnelName)}
          options={[
            { value: "identity_board", label: "identity_board" },
            { value: "event_attendance", label: "event_attendance" },
            { value: "institution_lifecycle", label: "institution_lifecycle" },
            {
              value: "event_organizer_lifecycle",
              label: "event_organizer_lifecycle",
            },
          ]}
        />
        <InputField
          label="Step name"
          placeholder="e.g. first_match_viewed"
          required
          value={step}
          onChange={(e) => setStep(e.target.value)}
        />
        <Button type="submit" isLoading={submitting} disabled={!step.trim()}>
          Send funnel event
        </Button>
      </form>
      {result && (
        <div className="mt-3 text-sm text-blue-700">{result}</div>
      )}
    </section>
  );
}
